import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, MessageSquare, Sparkles, Loader2, Check } from "lucide-react";

const ELIDO_LOGO = "https://customer-assets.emergentagent.com/job_custom-demo-gen/artifacts/x2wfas84_Elido_logotype_blue.png";
// Use the self-service onboarding entry so the prospect can build their
// OWN draft (admin still gets notified). Same flow our self-service users
// follow — keeps the two paths converged.
const TRIAL_URL = "/skapa-ai-agent";


/**
 * Minimal "vill gå vidare"-formulär längst ner på demo-sidan.
 * En knapp, ett textfält. Submit:as till /api/demo/{slug}/interest.
 */
function DemoInterestForm({ slug }) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || state === "sending") return;
    setState("sending");
    setErrMsg("");
    try {
      const api = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${api}/api/demo/${encodeURIComponent(slug)}/interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), contact: contact.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setState("sent");
    } catch (err) {
      setState("error");
      setErrMsg(err.message || String(err));
    }
  };

  return (
    <section className="py-16 lg:py-20 bg-[#F8FAFB]" data-testid="demo-interest-section">
      <div className="max-w-xl mx-auto px-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 text-center shadow-sm">
          {state === "sent" ? (
            <div data-testid="demo-interest-sent">
              <div className="w-14 h-14 mx-auto bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4">
                <Check className="w-7 h-7" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                Tack — vi hör av oss inom kort!
              </h2>
              <p className="text-slate-600 text-sm">
                Vi kommer att kontakta dig med nästa steg.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2 leading-snug">
                Vill du komma igång?
              </h2>
              <p className="text-slate-600 text-sm mb-6">
                Lämna ditt namn så hör vi av oss och aktiverar er AI-agent —
                helt gratis i 14 dagar.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ditt namn *"
                  maxLength={120}
                  disabled={state === "sending"}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3A8A98]/40"
                  data-testid="demo-interest-name"
                />
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="Telefon eller e-post (frivilligt)"
                  maxLength={200}
                  disabled={state === "sending"}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3A8A98]/40"
                  data-testid="demo-interest-contact"
                />
                <button
                  type="submit"
                  disabled={!name.trim() || state === "sending"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#255E69] hover:bg-[#3A8A98] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white font-semibold text-base px-6 py-3.5 rounded-lg"
                  data-testid="demo-interest-submit"
                >
                  {state === "sending" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Skickar…</>
                  ) : (
                    <>Jag vill gå vidare helt gratis! <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </form>
              {state === "error" && (
                <p className="mt-3 text-xs text-red-600" data-testid="demo-interest-error">
                  Kunde inte skicka — {errMsg}. Försök igen.
                </p>
              )}
              <p className="mt-4 text-[11px] text-slate-400">
                Inget kortkrav · Ingen bindningstid · GDPR-säker
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Public per-client chatbot demo page.
 * Route: /demo/chatbot/:slug
 *
 * Loads the demo metadata via `GET /api/demo/chatbot/{slug}`, then dynamically
 * injects /widget.js with the resolved client_id so the prospect can chat with
 * THEIR own configured bot. The Elido-owned origin bypasses `allowed_domain`
 * checks via the backend `_is_demo_origin` helper.
 */
export default function ChatbotDemoPage() {
  const { slug } = useParams();
  const [demo, setDemo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1. Fetch demo metadata
  useEffect(() => {
    let cancelled = false;
    const api = process.env.REACT_APP_BACKEND_URL;
    async function load() {
      try {
        const res = await fetch(`${api}/api/demo/chatbot/${encodeURIComponent(slug)}`);
        if (!res.ok) {
          setError(res.status === 404 ? "Demo hittades inte" : "Något gick fel");
          return;
        }
        const data = await res.json();
        if (!cancelled && data?.success && data?.demo) {
          setDemo(data.demo);
        } else if (!cancelled) {
          setError("Demo hittades inte");
        }
      } catch (e) {
        if (!cancelled) setError("Kunde inte nå servern just nu");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // 2. Inject widget.js once we have the client_id
  useEffect(() => {
    if (!demo?.client_id) return;
    // Defensive cleanup of any prior widget instance from this same SPA route
    document.querySelectorAll('script[data-elido-demo-widget="1"]').forEach(s => s.remove());

    const script = document.createElement("script");
    script.src = "/widget.js";
    script.async = true;
    script.defer = true;
    script.setAttribute("data-client-id", demo.client_id);
    script.setAttribute("data-demo", "1");
    script.setAttribute("data-elido-demo-widget", "1");
    document.body.appendChild(script);

    return () => {
      // Best-effort cleanup when leaving the route
      document.querySelectorAll('script[data-elido-demo-widget="1"]').forEach(s => s.remove());
      const root = document.getElementById("elido-widget-root");
      if (root) root.remove();
    };
  }, [demo?.client_id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F2A30] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (error || !demo) {
    return (
      <div className="min-h-screen bg-[#0F2A30] flex items-center justify-center px-6">
        <Helmet>
          <meta name="robots" content="noindex,nofollow" />
          <title>Demo hittades inte | Elido</title>
        </Helmet>
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-6">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">{error || "Demo hittades inte"}</h1>
          <p className="text-gray-400 mb-6">Den här demolänken är inte längre aktiv. Kontakta din Elido-kontakt eller besök elido.nu.</p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#3A8A98] hover:text-white transition-colors">
            ← Tillbaka till elido.nu
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Inter']" data-testid="chatbot-demo-page">
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="googlebot" content="noindex,nofollow" />
        <title>{demo.headline}</title>
        <meta name="description" content={demo.description} />
      </Helmet>

      {/* ── Hero: kundens skärmavbild dominerar hela första vyn ─────────
          Ingen Elido-header ovanpå — vi vill att besökaren får ett "wow,
          det är ju min hemsida"-ögonblick direkt. En diskret floating
          Elido-badge top-left + ett glasmorft förklarings-kort i botten av
          skärmen säger vad sidan är. Widgeten flyter över allt. */}
      <section
        className="relative overflow-hidden min-h-screen flex items-end"
        style={{
          background: demo.screenshot_url
            ? "#0a1418"
            : "linear-gradient(to bottom, #0F2A30, #143840)",
        }}
        data-testid="demo-hero"
      >
        {demo.screenshot_url ? (
          <>
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${demo.screenshot_url})`,
                backgroundSize: "cover",
                backgroundPosition: "top center",
              }}
              aria-hidden="true"
            />
            {/* Mjuk botten-vinjett så förklarings-kortet är läsbart utan
                att skymma bilden. Toppen är helt clear → wow-känsla. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(10,20,24,0) 0%, rgba(10,20,24,0) 45%, rgba(10,20,24,0.55) 80%, rgba(10,20,24,0.85) 100%)",
              }}
              aria-hidden="true"
            />
          </>
        ) : (
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
        )}

        {/* Floating Elido badge top-left (diskret, klickbar tillbaka) */}
        <a
          href="https://elido.nu"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-5 left-5 z-20 inline-flex items-center gap-2 bg-black/40 backdrop-blur-md border border-white/15 rounded-full px-3 py-1.5 hover:bg-black/55 transition-colors"
          data-testid="demo-floating-badge"
        >
          <img src={ELIDO_LOGO} alt="Elido" className="h-5" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-gray-200 font-semibold hidden sm:inline">
            Personlig demo
          </span>
        </a>

        {/* Förklarings-kort längst ner — "wow"-ögonblicket kommer FÖRE */}
        <div className="relative w-full pb-16 lg:pb-24 z-10">
          <div className="max-w-3xl mx-auto px-6">
            <div className="bg-black/55 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-7 md:px-10 md:py-9 text-center shadow-2xl">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-5">
                <Sparkles className="w-3.5 h-3.5 text-[#3A8A98]" />
                <span className="text-xs uppercase tracking-[0.18em] text-gray-200 font-semibold">
                  Live-demo av er AI-agent
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-white leading-[1.1] tracking-tight mb-4" data-testid="demo-headline">
                {demo.headline}
              </h1>
              <p className="text-base md:text-lg text-gray-200 leading-relaxed max-w-2xl mx-auto" data-testid="demo-description">
                {demo.description}
              </p>
              <p className="mt-3 text-sm text-gray-300/90 max-w-xl mx-auto italic">
                Så här kan er AI-agent se ut på er hemsida.
              </p>
              {(demo.industry || demo.city) && (
                <div className="mt-4 inline-flex items-center gap-2 text-sm text-gray-300">
                  {demo.industry && <span>{demo.industry}</span>}
                  {demo.industry && demo.city && <span className="opacity-50">·</span>}
                  {demo.city && <span>{demo.city}</span>}
                </div>
              )}
              <div className="mt-5 inline-flex items-center gap-2 text-xs text-gray-300/80">
                <MessageSquare className="w-3.5 h-3.5 text-[#3A8A98]" />
                Klicka chatt-bubblan nere till höger för att testa
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Test questions */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#255E69] font-bold mb-3">
              Testa direkt
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Öppna chatten och ställ en fråga
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Klicka på chatt-bubblan nere till höger och prova någon av dessa frågor — eller skriv en helt egen.
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 gap-3" data-testid="demo-test-questions">
            {demo.test_questions.map((q, i) => (
              <li
                key={i}
                className="group flex items-start gap-3 bg-gray-50 hover:bg-[#3A8A98]/5 border border-gray-200 hover:border-[#3A8A98]/30 rounded-xl px-5 py-4 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-[#255E69]">
                  {i + 1}
                </div>
                <span className="text-gray-800 leading-relaxed text-sm md:text-base">{q}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex items-center justify-center">
            <div className="inline-flex items-center gap-3 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-5 py-2.5">
              <MessageSquare className="w-4 h-4 text-[#255E69]" />
              Chattbubblan visas nere i hörnet
            </div>
          </div>
        </div>
      </section>

      {/* CTA — minimalt intresseformulär. Tidigare hade vi en stor
          "Testa 14 dagar gratis"-knapp som ledde till /skapa-ai-agent,
          men besökaren är ju redan inne i en personlig demo. Här räcker
          det med namn + en knapp så hör Elido-teamet av sig. */}
      {!demo.sandbox && <DemoInterestForm slug={demo.slug} />}

      {/* Footer */}
      <footer className="bg-[#0F2A30] py-10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <a href="https://elido.nu" target="_blank" rel="noopener noreferrer" className="inline-block mb-3">
            <img src={ELIDO_LOGO} alt="Elido" className="h-8 mx-auto opacity-80" />
          </a>
          <p className="text-gray-500 text-xs">
            Drivs av Elido AI · AI-agent för verksamheter som vill svara snabbare
          </p>
        </div>
      </footer>
    </div>
  );
}
